import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { showToast } from 'vant'
import { useI18n } from 'vue-i18n'
import { apiGetWorkList } from '@/apis/task'
import { EnumTaskAssetsType, EnumTaskStatus } from '@/enums/task'
import type { IResWorkListItem, WorkRecord } from '@/types/apis/task'
import { onProjectTaskStatusChanged } from '@/websocket/bus'

const PAGE_SIZE = 20

const mapTask = (task: IResWorkListItem): WorkRecord => ({
  id: `work-${task.Id}`,
  taskId: Number(task.TaskId),
  workId: task.Id,
  status: task.Status,
  CreateTime: task.CreateTime,
  results: {
    videoUrl: task.SaveUrl || undefined,
    videoCover: task.VideoCover || undefined,
  },
  width: task.Width,
  height: task.Height,
  duration: task.VideoDuration,
})

export const useWorksStore = defineStore('works', () => {
  const records = ref<WorkRecord[]>([])
  const loading = ref(false)
  const loaded = ref(false)
  const hasMore = ref(true)
  const page = ref(1)
  const realtimeSyncing = ref(false)

  let workStatusUnsubscribe: (() => void) | null = null

  const list = computed(() => records.value)

  const upsertTask = (task: WorkRecord) => {
    const index = records.value.findIndex(item => item.taskId === task.taskId)
    if (index >= 0) {
      records.value.splice(index, 1, task)
      return
    }

    records.value.push(task)
  }

  const removeTask = (taskId: number) => {
    const index = records.value.findIndex(item => item.taskId === taskId)
    if (index >= 0) {
      records.value.splice(index, 1)
    }
  }

  const loadList = async (pageNum?: number) => {
    if (pageNum !== undefined) {
      page.value = pageNum
      if (pageNum === 1) {
        records.value = []
        hasMore.value = true
      }
    }

    if (loading.value || !hasMore.value) {
      return
    }

    try {
      loading.value = true
      const response = await apiGetWorkList({
        PageIndex: page.value,
        PageSize: PAGE_SIZE,
        ProjectFileTypeId: EnumTaskAssetsType.VIDEO,
        Status: EnumTaskStatus.SUCCESS,
      })

      if (response.IsSuccess && response.Body) {
        response.Body.Items.map(mapTask).forEach(upsertTask)
        hasMore.value = response.Body.HasNextPage
        page.value += 1
        loaded.value = true
      }
    } catch (error) {
      console.error('加载作品列表失败:', error)
      const { t } = useI18n()
      showToast(t('common.errors.loadFailed'))
    } finally {
      loading.value = false
    }
  }

  const findTaskById = (taskId: number) => {
    return records.value.find(task => task.taskId === taskId) || null
  }

  const refreshList = async () => {
    await loadList(0)
  }

  const startRealtimeSync = () => {
    if (workStatusUnsubscribe) {
      return
    }

    workStatusUnsubscribe = onProjectTaskStatusChanged(payload => {
      if (payload.status !== EnumTaskStatus.SUCCESS) {
        return
      }

      void refreshList()
    })
    realtimeSyncing.value = true
  }

  const stopRealtimeSync = () => {
    workStatusUnsubscribe?.()
    workStatusUnsubscribe = null
    realtimeSyncing.value = false
  }

  const clear = () => {
    records.value = []
    loading.value = false
    loaded.value = false
    hasMore.value = true
    page.value = 1
    stopRealtimeSync()
  }

  return {
    list,
    loading,
    loaded,
    hasMore,
    page,
    realtimeSyncing,
    upsertTask,
    removeTask,
    loadList,
    refreshList,
    startRealtimeSync,
    stopRealtimeSync,
    findTaskById,
    clear,
  }
})
